
import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

interface Event {
  id: number;
  title: string;
  date: string;
  day: string;
  month: string;
  time: string;
  location: string;
  description: string;
  category: string;
  image: string;
}

const UpcomingEvents = () => {
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const events: Event[] = [
    {
      id: 1,
      title: 'Sunday Worship Service',
      date: 'Every Sunday',
      day: 'SUN',
      month: 'Weekly',
      time: '10:00 AM - 3:00 PM',
      location: 'The Kings Centre, Mansfield',
      description:
        'Join us for spirit-filled English and Akan worship services every Sunday.',
      category: 'Worship',
      image:
        'https://public.readdy.ai/ai/img_res/78fdafcf6ae5cf276d15170c719e07d1.jpg',
    },
    {
      id: 2,
      title: 'Midweek Prayer Meeting',
      date: 'Every Wednesday',
      day: 'WED',
      month: 'Weekly',
      time: '7:00 PM - 8:30 PM',
      location: 'The Kings Centre, Mansfield',
      description:
        'Come together in prayer for our church, families, and community.',
      category: 'Prayer',
      image:
        'https://readdy.ai/api/search-image?query=a%20peaceful%20intimate%20prayer%20gathering%20with%20people%20holding%20hands%20in%20a%20circle%20praying%20together%2C%20soft%20warm%20candlelight%20and%20serene%20atmosphere%2C%20diverse%20group%20united%20in%20prayer%2C%20gentle%20ambient%20lighting%20with%20warm%20golden%20tones%2C%20calm%20and%20spiritual%20setting%20in%20a%20modern%20church%20room&width=600&height=400&seq=upcevt2&orientation=landscape',
    },
    {
      id: 3,
      title: 'Youth Fellowship Night',
      date: 'Every Friday',
      day: 'FRI',
      month: 'Weekly',
      time: '6:30 PM - 8:30 PM',
      location: 'The Kings Centre, Mansfield',
      description:
        'Dynamic worship, games, and Bible teaching for young people.',
      category: 'Youth',
      image:
        'https://readdy.ai/api/search-image?query=energetic%20diverse%20youth%20group%20of%20teenagers%20and%20young%20adults%20gathered%20together%20laughing%20and%20connecting%20at%20a%20modern%20church%20youth%20night%2C%20warm%20string%20lights%20and%20contemporary%20decor%2C%20young%20people%20engaged%20in%20fellowship%20and%20fun%20activities%2C%20warm%20amber%20and%20cream%20color%20palette%2C%20vibrant%20and%20welcoming%20atmosphere&width=600&height=400&seq=upcevt3&orientation=landscape',
    },
    {
      id: 4,
      title: 'Community Outreach Day',
      date: 'Last Saturday of Month',
      day: 'SAT',
      month: 'Monthly',
      time: '10:00 AM - 2:00 PM',
      location: 'Various Locations, Mansfield',
      description:
        'Serve our neighbours through food distribution and community care.',
      category: 'Outreach',
      image:
        'https://readdy.ai/api/search-image?query=diverse%20church%20volunteers%20serving%20the%20local%20community%20with%20food%20parcels%20and%20supplies%2C%20people%20smiling%20and%20helping%20neighbours%20outdoors%2C%20warm%20and%20compassionate%20community%20service%20event%2C%20volunteers%20wearing%20matching%20shirts%20working%20together%2C%20warm%20golden%20hour%20lighting%20with%20cream%20and%20amber%20tones&width=600&height=400&seq=upcevt4&orientation=landscape',
    },
    {
      id: 5,
      title: "Women's Fellowship Brunch",
      date: 'First Saturday of Month',
      day: 'SAT',
      month: 'Monthly',
      time: '10:00 AM - 12:00 PM',
      location: 'The Kings Centre, Mansfield',
      description:
        'Connect with other women through food, fellowship, and inspiring messages.',
      category: 'Fellowship',
      image:
        'https://readdy.ai/api/search-image?query=elegant%20women%20gathering%20for%20a%20church%20brunch%20fellowship%20with%20beautiful%20table%20setting%20and%20flowers%2C%20diverse%20ladies%20laughing%20and%20connecting%20over%20a%20meal%20together%2C%20warm%20and%20inviting%20atmosphere%20with%20natural%20light%20streaming%20in%2C%20soft%20cream%20and%20gold%20color%20palette%2C%20joyful%20sisterhood%20moment&width=600&height=400&seq=upcevt5&orientation=landscape',
    },
  ];

  /** Check scroll position and enable/disable navigation buttons */
  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 10);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  /** Attach scroll listener once */
  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.addEventListener('scroll', checkScroll);
      // Initial check in case content is already overflowed
      checkScroll();
      return () => el.removeEventListener('scroll', checkScroll);
    }
  }, []);

  /** Scroll the container left or right by a fixed card width */
  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const cardWidth = 380; // matches the card's min‑width + gap
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -cardWidth : cardWidth,
        behavior: 'smooth',
      });
    }
  };

  return (
    <section className="py-24 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-end justify-between mb-14">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-[#0842A0] text-sm font-semibold tracking-wider uppercase mb-3">
              What&apos;s Happening
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-black font-['Playfair_Display']">
              Upcoming Events
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="hidden md:flex items-center gap-3"
          >
            <button
              onClick={() => scroll('left')}
              className={`w-12 h-12 rounded-full border flex items-center justify-center transition-all cursor-pointer ${
                canScrollLeft
                  ? 'border-gray-300 text-black hover:bg-black hover:text-white hover:border-black'
                  : 'border-gray-200 text-gray-300 cursor-not-allowed'
              }`}
              disabled={!canScrollLeft}
              aria-label="Previous events"
            >
              <i className="ri-arrow-left-s-line text-xl"></i>
            </button>
            <button
              onClick={() => scroll('right')}
              className={`w-12 h-12 rounded-full border flex items-center justify-center transition-all cursor-pointer ${
                canScrollRight
                  ? 'border-gray-300 text-black hover:bg-black hover:text-white hover:border-black'
                  : 'border-gray-200 text-gray-300 cursor-not-allowed'
              }`}
              disabled={!canScrollRight}
              aria-label="Next events"
            >
              <i className="ri-arrow-right-s-line text-xl"></i>
            </button>
          </motion.div>
        </div>

        {/* Scrollable Cards */}
        <div
          ref={scrollRef}
          className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {events.map((event, index) => (
            <motion.div
              key={event.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.08 }}
              className="min-w-[340px] max-w-[340px] snap-start bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 group flex-shrink-0"
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={event.image}
                  alt={event.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-4 left-4 bg-white rounded-lg px-3 py-2 text-center shadow-md min-w-[56px]">
                  <span className="block text-xs font-bold text-[#0842A0] uppercase leading-tight">
                    {event.day}
                  </span>
                  <span className="block text-[10px] text-gray-500 font-medium uppercase leading-tight mt-0.5">
                    {event.month}
                  </span>
                </div>
                <div className="absolute top-4 right-4">
                  <span className="bg-black/80 text-white px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide">
                    {event.category}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-5">
                <h3 className="text-lg font-bold text-black mb-2 leading-snug group-hover:text-[#0842A0] transition-colors">
                  {event.title}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed mb-4 line-clamp-2">
                  {event.description}
                </p>

                <div className="space-y-2 mb-5">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-time-line text-[#0842A0]"></i>
                    </div>
                    <span>{event.time}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-map-pin-2-line text-[#0842A0]"></i>
                    </div>
                    <span>{event.location}</span>
                  </div>
                </div>

                <a
                  href="/events"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/events');
                  }}
                  className="inline-flex items-center text-sm font-semibold text-black hover:text-[#0842A0] transition-colors cursor-pointer group/link"
                >
                  Learn More
                  <i className="ri-arrow-right-line ml-1.5 group-hover/link:translate-x-1 transition-transform"></i>
                </a>
              </div>
            </motion.div>
          ))}
        </div>

        {/* View All Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mt-12"
        >
          <a
            href="/events"
            onClick={(e) => {
              e.preventDefault();
              navigate('/events');
            }}
            className="inline-flex items-center gap-2 bg-black hover:bg-gray-900 text-white px-8 py-3 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
          >
            View All Events
            <i className="ri-arrow-right-line"></i>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default UpcomingEvents;
